
import Link from '@/components/compat/Link';
import { useRouter } from '@/lib/navigation';
import { useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  ExternalLink,
  FileText,
  Frame,
  HelpCircle,
  Layers,
  ListChecks,
  Loader2,
  Palette,
  Plus,
  Sparkles,
  Star,
  Store,
  Tags,
  Trash2,
  LayoutGrid,
  SearchCode,
  UserCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { AceScope, AceStatus } from '@/lib/aces-data';
import {
  ACE_ICON_OPTIONS,
  aceToEditorInput,
  apiPowerToAceEditorPatch,
  emptyAceEditorInput,
  slugifyAceName,
  type AceEditorInput,
} from '@/lib/aces-cms';
import { fetchPowerInfo } from '@/lib/powers-data';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelPageBackButtonClass,
  painelSelectContentClass,
  painelSurfaceClass,
} from '@/components/painel/painel-styles';
import {
  painelInputClass,
  painelSelectTriggerClass,
  painelTextareaClass,
} from '@/components/painel/cms/painel-form';
import { accountSwitchClass } from '@/components/painel/account-settings/account-settings-shared';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';
import { SmilieCodeField } from '@/components/painel/cms/SmilieCodeField';
import { AceAccentColorPicker } from '@/components/painel/aces/AceAccentColorPicker';
import { AvatarFramesEditor } from '@/components/painel/cms/AvatarFramesEditor';
import { PowerPstyleEditor } from '@/components/painel/powers/PowerPstyleEditor';
import { EntitySeoSection } from '@/components/painel/seo/EntitySeoSection';
import { useAcesCms, useCatalogAces } from '@/components/painel/aces/aces-cms-context';
import { RelatedExploreEditorField } from '@/components/painel/cms/RelatedExploreEditorField';
import { emptyRelatedExplore } from '@/lib/related-explore';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { sessionHasPermission } from '@/lib/painel-rbac';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { logPainelEditorPublicationActivity } from '@/lib/painel-activity-log';
import { resolvePublicationOnSave } from '@/lib/cms-publication';
import { cn } from '@/lib/utils';

type PainelAceEditorPageProps = { mode: 'new' } | { mode: 'edit'; aceSlug: string };

const API_FETCH_DEBOUNCE_MS = 650;

function SectionCard({
  title,
  description,
  icon: Icon,
  accentClass = 'bg-sky-500/15 text-sky-300 ring-sky-400/25',
  children,
  className,
}: {
  title: string;
  description?: string;
  icon: LucideIcon;
  accentClass?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(painelSurfaceClass, 'overflow-hidden p-0', className)}>
      <div className="flex items-start gap-4 border-b border-white/10 bg-gradient-to-r from-white/[0.04] to-transparent px-5 py-4 md:px-6">
        <span
          className={cn(
            'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1',
            accentClass
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-poppins text-lg font-semibold text-white">{title}</h2>
          {description ? (
            <p className="mt-1 font-inter text-sm leading-relaxed text-slate-400">{description}</p>
          ) : null}
        </div>
      </div>
      <div className="space-y-4 p-5 md:p-6">{children}</div>
    </div>
  );
}

export function PainelAceEditorPage(props: PainelAceEditorPageProps) {
  const router = useRouter();
  const { session, role, hydrated: sessionHydrated } = usePainelSession();
  const { getAceBySlug, addAce, updateAce, hydrated } = useAcesCms();
  const { aces: catalogAces } = useCatalogAces();
  const { showSaveSuccess } = usePainelSaveFeedback();
  const [form, setForm] = useState<AceEditorInput>(emptyAceEditorInput);
  const [slugTouched, setSlugTouched] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const lastFetchedSlugRef = useRef<string | null>(null);
  const fetchRequestRef = useRef(0);
  const seededSlugRef = useRef<string | null>(null);

  const editingSlug = props.mode === 'edit' ? props.aceSlug.toLowerCase() : null;
  const editorResetKey = editingSlug ?? 'new';
  const existing = editingSlug ? getAceBySlug(editingSlug) : undefined;

  useEffect(() => {
    if (!sessionHydrated) return;
    const allowed =
      props.mode === 'new'
        ? sessionHasPermission(session, 'aces.create')
        : sessionHasPermission(session, 'aces.edit');
    if (!allowed) {
      router.replace('/painel/aces');
    }
  }, [sessionHydrated, session, props.mode, router]);

  useEffect(() => {
    if (!hydrated || props.mode !== 'edit' || !editingSlug) return;
    if (seededSlugRef.current === editingSlug) return;
    const ace = getAceBySlug(editingSlug);
    if (ace) {
      setForm(aceToEditorInput(ace));
      setSlugTouched(true);
      seededSlugRef.current = editingSlug;
    }
  }, [hydrated, props.mode, editingSlug, getAceBySlug]);

  useEffect(() => {
    if (props.mode !== 'new') return;

    const slug = slugTouched ? form.slug.trim().toLowerCase() : slugifyAceName(form.name);
    if (!slug || slug.length < 2) {
      setApiLoading(false);
      setApiMessage(null);
      return;
    }

    if (lastFetchedSlugRef.current === slug) return;

    const timer = window.setTimeout(async () => {
      const requestId = ++fetchRequestRef.current;
      setApiLoading(true);
      setApiMessage(null);

      try {
        const data = await fetchPowerInfo(slug);
        if (requestId !== fetchRequestRef.current) return;

        if (!data) {
          setApiMessage('Ace não encontrado na API — preencha os campos manualmente.');
          lastFetchedSlugRef.current = slug;
          return;
        }

        const patch = apiPowerToAceEditorPatch(data);
        setForm((prev) => ({
          ...prev,
          ...patch,
          slug: slugTouched ? prev.slug : patch.slug ?? prev.slug,
        }));
        lastFetchedSlugRef.current = slug;
        setApiMessage(`Dados de “${data.name}” importados da API xatBlog.`);
      } catch {
        if (requestId === fetchRequestRef.current) {
          setApiMessage('Não foi possível consultar a API. Tente novamente ou preencha manualmente.');
        }
      } finally {
        if (requestId === fetchRequestRef.current) {
          setApiLoading(false);
        }
      }
    }, API_FETCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [props.mode, form.name, form.slug, slugTouched]);

  const patch = (next: Partial<AceEditorInput>) => {
    setForm((f) => {
      const merged = { ...f, ...next };
      if ('name' in next && !slugTouched && props.mode === 'new') {
        const nextSlug = slugifyAceName(next.name ?? f.name);
        if (nextSlug !== lastFetchedSlugRef.current) {
          lastFetchedSlugRef.current = null;
        }
        merged.slug = nextSlug;
      }
      if ('slug' in next && props.mode === 'new') {
        const key = (next.slug ?? f.slug).trim().toLowerCase();
        if (key !== lastFetchedSlugRef.current) {
          lastFetchedSlugRef.current = null;
        }
      }
      return merged;
    });
  };

  const toggleScope = (scope: AceScope) => {
    setForm((f) => {
      const has = f.scopes.includes(scope);
      const scopes = has ? f.scopes.filter((s) => s !== scope) : [...f.scopes, scope];
      return { ...f, scopes: scopes.length > 0 ? scopes : ['account'] };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim()) return;
    const publicationStatus = resolvePublicationOnSave(role);
    const previousStatus = existing?.publicationStatus ?? null;
    const payload = { ...form, publicationStatus };
    try {
      if (props.mode === 'new') {
        const created = await addAce(payload);
        logPainelEditorPublicationActivity(session, {
          resource: 'ace',
          resourceId: created.slug,
          resourceLabel: form.name.trim(),
          isNew: true,
          previousStatus: null,
          nextStatus: publicationStatus,
        });
        showSaveSuccess({
          title: 'Ace criado',
          description:
            publicationStatus === 'pending_review'
              ? 'O ace foi enviado para revisão antes de ir ao ar.'
              : 'As alterações já estão publicadas no catálogo.',
        });
        router.replace(`/painel/aces/${created.slug}/edit`);
      } else if (editingSlug) {
        await updateAce(editingSlug, payload);
        logPainelEditorPublicationActivity(session, {
          resource: 'ace',
          resourceId: editingSlug,
          resourceLabel: form.name.trim(),
          isNew: false,
          previousStatus,
          nextStatus: publicationStatus,
        });
        showSaveSuccess({
          title: 'Ace atualizado',
          description:
            publicationStatus === 'pending_review'
              ? 'O ace foi enviado para revisão antes de ir ao ar.'
              : 'As alterações já estão publicadas no catálogo.',
        });
      }
    } catch {
      /* toast já emitido no CMS */
    }
  };

  if (props.mode === 'edit' && hydrated && !existing) {
    return (
      <div className="space-y-4">
        <p className="font-inter text-slate-400">Ace não encontrado.</p>
        <Button asChild variant="ghost" className={painelPageBackButtonClass}>
          <Link href="/painel/aces">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button variant="ghost" asChild className={painelPageBackButtonClass}>
            <Link href="/painel/aces">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Aces
            </Link>
          </Button>
          <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {props.mode === 'new' ? 'Novo Ace' : `Editar ${existing?.name ?? form.name}`}
          </h1>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Conteúdo exibido em /aces e /aces/[slug] no site público.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.slug ? (
            <Button
              type="button"
              variant="outline"
              className={painelButtonOutlineClass}
              asChild
            >
              <a href={`/aces/${form.slug}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Ver no site
              </a>
            </Button>
          ) : null}
          <Button
            type="button"
            className={painelButtonPrimaryClass}
            onClick={handleSave}
            disabled={!form.name.trim() || !form.slug.trim() || apiLoading}
          >
            Salvar
          </Button>
        </div>
      </div>

      {props.mode === 'new' && (apiLoading || apiMessage) ? (
        <div
          className={cn(
            'flex items-start gap-3 rounded-xl border px-4 py-3 font-inter text-sm',
            apiLoading
              ? 'border-sky-400/25 bg-sky-500/10 text-sky-100'
              : apiMessage?.includes('importados')
                ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100'
                : 'border-white/10 bg-white/[0.04] text-slate-300'
          )}
        >
          {apiLoading ? (
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-sky-300" />
          ) : (
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
          )}
          <p>{apiLoading ? 'A consultar a API xatBlog…' : apiMessage}</p>
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <SectionCard
          icon={UserCircle}
          accentClass="bg-violet-500/15 text-violet-300 ring-violet-400/25"
          title="Identidade"
          description="Nome, slug e estado de publicação."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label className="text-blue-100/90">Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  patch({ slug: e.target.value.toLowerCase() });
                }}
                className={cn(painelInputClass, 'font-mono')}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => patch({ status: v as AceStatus })}
              >
                <SelectTrigger className={painelSelectTriggerClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={painelSelectContentClass}>
                  <SelectItem value="active" className="focus:bg-white/10 focus:text-white">
                    Ativo
                  </SelectItem>
                  <SelectItem value="legacy" className="focus:bg-white/10 focus:text-white">
                    Legado
                  </SelectItem>
                  <SelectItem value="upcoming" className="focus:bg-white/10 focus:text-white">
                    Em breve
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3 sm:col-span-2">
              <div>
                <p className="font-poppins text-sm font-medium text-white">Marcar como novo</p>
                <p className="font-inter text-xs text-slate-400">Exibe badge &quot;Novo&quot; no catálogo.</p>
              </div>
              <Switch
                checked={form.isNew}
                onCheckedChange={(v) => patch({ isNew: v })}
                className={accountSwitchClass}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Store}
          accentClass="bg-emerald-500/15 text-emerald-300 ring-emerald-400/25"
          title="Loja xat"
          description="Preço, duração e referências oficiais."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label className="text-blue-100/90">ID do Ace</Label>
              <Input
                type="number"
                value={form.powerId}
                onChange={(e) =>
                  patch({ powerId: e.target.value === '' ? '' : Number(e.target.value) })
                }
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2 sm:col-span-1">
              <SmilieCodeField
                id="ace-smiley-code"
                label="Smilie exclusivo"
                value={form.smileyCode}
                onChange={(smileyCode) => patch({ smileyCode })}
                placeholder={form.slug || 'ace'}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Preço (xats)</Label>
              <Input
                type="number"
                min={0}
                value={form.storePriceXats}
                onChange={(e) => patch({ storePriceXats: Number(e.target.value) })}
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Duração (dias)</Label>
              <Input
                type="number"
                min={1}
                value={form.subscriptionDays}
                onChange={(e) => patch({ subscriptionDays: Number(e.target.value) })}
                className={painelInputClass}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={FileText}
          accentClass="bg-sky-500/15 text-sky-300 ring-sky-400/25"
          title="Descrições"
          description="Resumo e texto longo da página de detalhe."
          className="xl:col-span-2"
        >
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Descrição curta</Label>
              <RichTextEditor
                value={form.shortDescription}
                onChange={(shortDescription) => patch({ shortDescription })}
                toolbar="extended"
                editorMinHeightClass="min-h-[100px]"
                resetKey={`${editorResetKey}-short`}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Sobre</Label>
              <RichTextEditor
                value={form.about}
                onChange={(about) => patch({ about })}
                toolbar="extended"
                editorMinHeightClass="min-h-[180px]"
                resetKey={`${editorResetKey}-about`}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Palette}
          accentClass="bg-pink-500/15 text-pink-300 ring-pink-400/25"
          title="Visual"
          description="Cores e ícone do cartão no catálogo."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <AceAccentColorPicker
                value={form.imageAccent}
                onChange={(imageAccent) => patch({ imageAccent })}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Ícone</Label>
              <Select
                value={form.visualIconId}
                onValueChange={(v) => patch({ visualIconId: v as AceEditorInput['visualIconId'] })}
              >
                <SelectTrigger className={painelSelectTriggerClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={painelSelectContentClass}>
                  {ACE_ICON_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.id}
                      value={opt.id}
                      className="focus:bg-white/10 focus:text-white"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2 sm:col-span-2">
              <Label className="text-blue-100/90">Gradiente CSS</Label>
              <Input
                value={form.visualGradient}
                onChange={(e) => patch({ visualGradient: e.target.value })}
                className={cn(painelInputClass, 'font-mono text-xs')}
              />
            </div>
            <div className="grid gap-2 sm:col-span-2">
              <Label className="text-blue-100/90">Glow (rgba)</Label>
              <Input
                value={form.visualGlow}
                onChange={(e) => patch({ visualGlow: e.target.value })}
                className={cn(painelInputClass, 'font-mono text-xs')}
              />
            </div>
            <div
              className="flex h-16 items-center justify-center rounded-xl sm:col-span-2"
              style={{ background: form.visualGradient }}
            >
              <span className="rounded-lg bg-black/25 px-3 py-1 font-inter text-xs text-white/90">
                Prévia do gradiente
              </span>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Tags}
          accentClass="bg-amber-500/15 text-amber-300 ring-amber-400/25"
          title="Metadados"
          description="Escopo, renovação e etiquetas exibidas no site."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Data de lançamento</Label>
              <Input
                type="date"
                value={form.releasedAt}
                onChange={(e) => patch({ releasedAt: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Popularidade</Label>
              <Input
                type="number"
                min={0}
                value={form.popularity}
                onChange={(e) => patch({ popularity: Number(e.target.value) })}
                className={painelInputClass}
              />
            </div>
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              {(['account', 'chat'] as AceScope[]).map((scope) => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => toggleScope(scope)}
                  className={cn(
                    'rounded-full px-3 py-1 font-inter text-xs font-semibold transition-colors',
                    form.scopes.includes(scope)
                      ? 'bg-violet-500/20 text-violet-200 ring-1 ring-violet-400/30'
                      : 'bg-white/[0.04] text-slate-400 ring-1 ring-white/10 hover:bg-white/[0.08]'
                  )}
                >
                  {scope === 'account' ? 'Conta' : 'Chat'}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3">
              <span className="font-inter text-sm text-slate-300">Com níveis</span>
              <Switch
                checked={form.hasLevels}
                onCheckedChange={(v) => patch({ hasLevels: v })}
                className={accountSwitchClass}
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3">
              <span className="font-inter text-sm text-slate-300">Renovação</span>
              <Switch
                checked={form.requiresRenewal}
                onCheckedChange={(v) => patch({ requiresRenewal: v })}
                className={accountSwitchClass}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Etiqueta de subscrição</Label>
              <Input
                value={form.subscriptionLabel}
                onChange={(e) => patch({ subscriptionLabel: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Etiqueta de renovação</Label>
              <Input
                value={form.renewalLabel}
                onChange={(e) => patch({ renewalLabel: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2 sm:col-span-2">
              <Label className="text-blue-100/90">Disponibilidade</Label>
              <Input
                value={form.availability}
                onChange={(e) => patch({ availability: e.target.value })}
                className={painelInputClass}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Star}
          accentClass="bg-yellow-500/15 text-yellow-200 ring-yellow-400/25"
          title="Destaques"
          description="Itens exibidos na secção de funcionalidades da página pública."
          className="xl:col-span-2"
        >
          <div className="space-y-3">
            {form.features.map((feature, index) => (
              <div
                key={feature.id}
                className="grid gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 md:grid-cols-[1fr_1fr_auto]"
              >
                <Input
                  value={feature.title}
                  onChange={(e) => {
                    const features = [...form.features];
                    features[index] = { ...feature, title: e.target.value };
                    patch({ features });
                  }}
                  placeholder="Título"
                  className={painelInputClass}
                />
                <Input
                  value={feature.description}
                  onChange={(e) => {
                    const features = [...form.features];
                    features[index] = { ...feature, description: e.target.value };
                    patch({ features });
                  }}
                  placeholder="Descrição"
                  className={painelInputClass}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0 text-rose-300 hover:bg-rose-500/15"
                  onClick={() =>
                    patch({ features: form.features.filter((_, i) => i !== index) })
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              className={painelButtonOutlineClass}
              onClick={() =>
                patch({
                  features: [
                    ...form.features,
                    { id: crypto.randomUUID(), title: '', description: '' },
                  ],
                })
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              Adicionar destaque
            </Button>
          </div>
        </SectionCard>

        <SectionCard
          icon={Frame}
          accentClass="bg-rose-500/15 text-rose-300 ring-rose-400/25"
          title="Avatar frames"
          description="Galeria de molduras exibida na página pública. Envie cada imagem pelo painel."
          className="xl:col-span-2"
        >
          <AvatarFramesEditor
            value={{
              enabled: form.avatarFramesEnabled,
              frames: form.avatarFrames,
            }}
            onChange={({ enabled, frames }) =>
              patch({ avatarFramesEnabled: enabled, avatarFrames: frames })
            }
          />
        </SectionCard>

        <SectionCard
          icon={ListChecks}
          accentClass="bg-cyan-500/15 text-cyan-300 ring-cyan-400/25"
          title="Passo a passo"
          description="Secção «Como funciona» na página de detalhe."
          className="xl:col-span-2"
        >
          <div className="space-y-3">
            {form.howItWorks.map((step, index) => (
              <div
                key={`step-${step.step}-${index}`}
                className="grid gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 md:grid-cols-[72px_1fr_1fr_auto]"
              >
                <div className="grid gap-1.5">
                  <Label className="text-xs text-blue-100/80">Passo</Label>
                  <Input
                    type="number"
                    min={1}
                    value={step.step}
                    onChange={(e) => {
                      const howItWorks = [...form.howItWorks];
                      howItWorks[index] = { ...step, step: Number(e.target.value) || 1 };
                      patch({ howItWorks });
                    }}
                    className={painelInputClass}
                  />
                </div>
                <Input
                  value={step.title}
                  onChange={(e) => {
                    const howItWorks = [...form.howItWorks];
                    howItWorks[index] = { ...step, title: e.target.value };
                    patch({ howItWorks });
                  }}
                  placeholder="Título do passo"
                  className={painelInputClass}
                />
                <Input
                  value={step.description}
                  onChange={(e) => {
                    const howItWorks = [...form.howItWorks];
                    howItWorks[index] = { ...step, description: e.target.value };
                    patch({ howItWorks });
                  }}
                  placeholder="Descrição"
                  className={painelInputClass}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0 text-rose-300 hover:bg-rose-500/15"
                  onClick={() =>
                    patch({ howItWorks: form.howItWorks.filter((_, i) => i !== index) })
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              className={painelButtonOutlineClass}
              onClick={() =>
                patch({
                  howItWorks: [
                    ...form.howItWorks,
                    {
                      step: form.howItWorks.length + 1,
                      title: '',
                      description: '',
                    },
                  ],
                })
              }
            >
              <ListChecks className="mr-2 h-4 w-4" />
              Adicionar passo
            </Button>
          </div>
        </SectionCard>

        <SectionCard
          icon={Palette}
          accentClass="bg-indigo-500/15 text-indigo-300 ring-indigo-400/25"
          title="Pstyle"
          description="Secção de tema de perfil na página pública. Desligada por padrão — ative para personalizar ou exibir tema built-in."
          className="xl:col-span-2"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3">
              <div>
                <p className="font-poppins text-sm font-medium text-white">Publicar secção Pstyle</p>
                <p className="font-inter text-xs text-slate-400">
                  Quando desligada, Aces como Yellow continuam a usar o tema built-in se existir.
                </p>
              </div>
              <Switch
                checked={form.pstyleSectionEnabled}
                onCheckedChange={(pstyleSectionEnabled) => patch({ pstyleSectionEnabled })}
                className={accountSwitchClass}
              />
            </div>
            {form.pstyleSectionEnabled ? (
              <PowerPstyleEditor
                value={form.pstyle}
                onChange={(pstyle) => patch({ pstyle })}
                avatarFallback={form.smileyCode || form.slug}
              />
            ) : (
              <p className="font-inter text-sm text-slate-500">
                Secção Pstyle desligada no CMS. O site pode ainda mostrar temas built-in (ex.: Sunburst no
                Yellow).
              </p>
            )}
          </div>
        </SectionCard>

        {form.hasLevels ? (
          <SectionCard
            icon={Layers}
            accentClass="bg-orange-500/15 text-orange-300 ring-orange-400/25"
            title="Níveis"
            description="Progressão da assinatura exibida na timeline."
            className="xl:col-span-2"
          >
            <div className="mb-4 grid gap-2 sm:max-w-xs">
              <Label className="text-blue-100/90">Etiqueta de níveis</Label>
              <Input
                value={form.levelsLabel}
                onChange={(e) => patch({ levelsLabel: e.target.value })}
                className={painelInputClass}
              />
            </div>
            <div className="space-y-3">
              {form.levels.map((level, index) => (
                <div
                  key={`level-${level.level}-${index}`}
                  className="space-y-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-4"
                >
                  <div className="grid gap-3 md:grid-cols-[80px_1fr_1fr_auto]">
                    <Input
                      type="number"
                      min={1}
                      value={level.level}
                      onChange={(e) => {
                        const levels = [...form.levels];
                        levels[index] = { ...level, level: Number(e.target.value) || 1 };
                        patch({ levels });
                      }}
                      placeholder="Nº"
                      className={painelInputClass}
                    />
                    <Input
                      value={level.name}
                      onChange={(e) => {
                        const levels = [...form.levels];
                        levels[index] = { ...level, name: e.target.value };
                        patch({ levels });
                      }}
                      placeholder="Nome do nível"
                      className={painelInputClass}
                    />
                    <Input
                      value={level.description}
                      onChange={(e) => {
                        const levels = [...form.levels];
                        levels[index] = { ...level, description: e.target.value };
                        patch({ levels });
                      }}
                      placeholder="Descrição"
                      className={painelInputClass}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 shrink-0 text-rose-300 hover:bg-rose-500/15"
                      onClick={() => patch({ levels: form.levels.filter((_, i) => i !== index) })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <textarea
                    value={level.perks.join('\n')}
                    onChange={(e) => {
                      const levels = [...form.levels];
                      levels[index] = {
                        ...level,
                        perks: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                      };
                      patch({ levels });
                    }}
                    placeholder="Vantagens (uma por linha)"
                    className={cn(painelTextareaClass, 'min-h-[72px]')}
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className={painelButtonOutlineClass}
                onClick={() =>
                  patch({
                    levels: [
                      ...form.levels,
                      {
                        level: form.levels.length + 1,
                        name: '',
                        description: '',
                        perks: [],
                      },
                    ],
                  })
                }
              >
                <Layers className="mr-2 h-4 w-4" />
                Adicionar nível
              </Button>
            </div>
          </SectionCard>
        ) : null}

        <SectionCard
          icon={HelpCircle}
          accentClass="bg-teal-500/15 text-teal-300 ring-teal-400/25"
          title="FAQ"
          description="Perguntas frequentes no final da página."
          className="xl:col-span-2"
        >
          <div className="space-y-3">
            {form.faq.map((item, index) => (
              <div
                key={item.id}
                className="grid gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-4"
              >
                <Input
                  value={item.question}
                  onChange={(e) => {
                    const faq = [...form.faq];
                    faq[index] = { ...item, question: e.target.value };
                    patch({ faq });
                  }}
                  placeholder="Pergunta"
                  className={painelInputClass}
                />
                <textarea
                  value={item.answer}
                  onChange={(e) => {
                    const faq = [...form.faq];
                    faq[index] = { ...item, answer: e.target.value };
                    patch({ faq });
                  }}
                  placeholder="Resposta"
                  className={cn(painelTextareaClass, 'min-h-[88px]')}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0 text-rose-300 hover:bg-rose-500/15"
                    onClick={() => patch({ faq: form.faq.filter((_, i) => i !== index) })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              className={painelButtonOutlineClass}
              onClick={() =>
                patch({
                  faq: [
                    ...form.faq,
                    { id: crypto.randomUUID(), question: '', answer: '' },
                  ],
                })
              }
            >
              <HelpCircle className="mr-2 h-4 w-4" />
              Adicionar pergunta
            </Button>
          </div>
        </SectionCard>

        <SectionCard
          icon={LayoutGrid}
          accentClass="bg-amber-500/15 text-amber-300 ring-amber-400/25"
          title="Explore mais"
          description="Secção no final da página pública com até 4 aces sugeridos. O botão «Ver todos» abre a listagem de Aces."
          className="xl:col-span-2"
        >
          <RelatedExploreEditorField
            value={form.relatedExplore ?? emptyRelatedExplore()}
            onChange={(relatedExplore) => patch({ relatedExplore })}
            catalog={catalogAces}
            currentSlug={form.slug}
            defaultTitle="Explore mais Aces"
            itemLabel="Nome ou slug do ace"
            helpText="Os cards usam imagem, descrição e preço do ace indicado. Se a lista estiver vazia, preenche automaticamente com outros aces."
          />
        </SectionCard>

        {sessionHasPermission(session, 'seo.manage') ? (
          <SectionCard
            icon={SearchCode}
            accentClass="bg-sky-500/15 text-sky-300 ring-sky-400/25"
            title="SEO"
            description="Title, description, Open Graph e robots desta página de Ace."
            className="xl:col-span-2"
          >
            <EntitySeoSection
              entityType="ace"
              entityId={
                props.mode === 'edit' && existing?.id != null && Number.isFinite(Number(existing.id))
                  ? Number(existing.id)
                  : null
              }
              enabled={props.mode === 'edit' && existing?.id != null && Number(existing.id) > 0}
              fallbackTitle={form.name}
              fallbackDescription={form.shortDescription}
              previewPath={form.slug ? `/aces/${form.slug}` : '/aces'}
            />
          </SectionCard>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          className={painelButtonPrimaryClass}
          onClick={handleSave}
          disabled={!form.name.trim() || !form.slug.trim() || apiLoading}
        >
          Salvar
        </Button>
        <Button type="button" variant="outline" className={painelButtonOutlineClass} asChild>
          <Link href="/painel/aces">Cancelar</Link>
        </Button>
      </div>
    </div>
  );
}
